import { Fragment, useEffect, useRef, useState } from "react";
import { ChevronRight, ChevronDown, Plus, X, Search, Check } from "lucide-react";
import { foldIncludes } from "../../lib/trSearch";

const TAB_ON = "bg-orange-500 border-orange-500 text-white";
const TAB_OFF = "border-orange-400 text-slate-800 hover:bg-orange-50";

// Şube hizmet başlığı sekmeleri + "Hizmet Ekle": Hizmetler kataloğundan (başlık → alt hizmet) seçim
export function CategoryTabs({ tabs, open, onToggle, services, addOpen, setAddOpen, onAddService }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {tabs.map((c) => (
        <button key={c.id} type="button" onClick={() => onToggle(c.id)} data-testid={`bs-tab-${c.id}`}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border-2 text-xs font-bold uppercase transition-colors ${open === c.id ? TAB_ON : TAB_OFF}`}>
          {c.name} {open === c.id ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        </button>
      ))}
      <div className="ml-auto relative">
        <button type="button" onClick={() => setAddOpen((v) => !v)} data-testid="bs-add-service" aria-expanded={addOpen} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border-2 border-slate-900 text-xs font-bold uppercase hover:bg-slate-100">Hizmet Ekle <Plus className="w-3.5 h-3.5" /></button>
        {addOpen && <AddServiceMenu services={services} onAdd={onAddService} onClose={() => setAddOpen(false)} />}
      </div>
    </div>
  );
}

// Hizmetler menüsündeki alt hizmetler, başlığa göre gruplu; yazarak ara, ↑↓ gez, Enter seç, ESC kapat
function AddServiceMenu({ services, onAdd, onClose }) {
  const [q, setQ] = useState("");
  const [hi, setHi] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => { setHi(0); }, [q]);
  useEffect(() => { listRef.current?.querySelector("[data-hl]")?.scrollIntoView({ block: "nearest" }); }, [hi]);
  const shown = services.filter((s) => !q.trim() || foldIncludes(`${s.category_name} ${s.name}`, q));
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(shown.length - 1, h + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(0, h - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); if (shown[hi]) onAdd(shown[hi]); }
    else if (e.key === "Escape") { e.stopPropagation(); onClose(); }
  };
  return (
    <div className="absolute right-0 mt-1 z-20 w-80 bg-white border border-slate-300 rounded-md shadow-lg" data-testid="bs-add-menu">
      <div className="flex items-center justify-between px-2 py-1.5 border-b border-slate-100">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Hizmet seç (Hizmetler menüsü)</span>
        <button type="button" onClick={onClose} data-testid="bs-add-close" title="Kapat (ESC)" className="p-0.5 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-900"><X className="w-3.5 h-3.5" /></button>
      </div>
      <div className="relative p-1.5 border-b border-slate-100">
        <Search className="w-3.5 h-3.5 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey} placeholder="Hizmet veya başlık ara…" data-testid="bs-add-search" className="w-full pl-7 pr-2 py-1.5 text-xs border border-slate-300 rounded-md bg-white outline-none focus:border-amber-500" />
      </div>
      <div ref={listRef} className="max-h-72 overflow-auto p-1" role="listbox">
        {shown.length === 0 && <div className="px-2 py-2 text-xs text-slate-500" data-testid="bs-add-empty">Eşleşen hizmet yok</div>}
        {shown.map((s, i) => (
          <Fragment key={s.id}>
            {(i === 0 || shown[i - 1].category_id !== s.category_id) && (
              <div className="px-2 pt-2 pb-0.5 text-[10px] font-bold uppercase tracking-wide text-orange-700 flex items-center gap-1.5">
                {s.category_name}
                {!s.in_project && <span className="normal-case font-semibold text-[9px] px-1 py-px rounded bg-slate-100 text-slate-600" title="Bu başlık projede henüz açık değil — bir alt hizmet seçince başlık projeye eklenir" data-testid={`bs-add-cat-new-${s.category_id}`}>projede yok · seçince eklenir</span>}
              </div>
            )}
            <button type="button" role="option" aria-selected={i === hi} data-hl={i === hi || undefined} onMouseEnter={() => setHi(i)} onClick={() => onAdd(s)} data-testid={`bs-add-svc-${s.id}`}
              className={`w-full flex items-center gap-2 text-left px-2 py-1.5 text-xs font-semibold rounded ${i === hi ? "bg-orange-100" : "hover:bg-orange-50"}`}>
              <span className="truncate">{s.name}</span>
              <span className="text-slate-400 font-normal">({s.unit})</span>
              {s.assigned && <span className="ml-auto inline-flex items-center gap-0.5 text-[10px] text-emerald-700 font-semibold shrink-0" title="Bu şubede taşeron ataması var"><Check className="w-3 h-3" /> atanmış</span>}
            </button>
          </Fragment>
        ))}
      </div>
    </div>
  );
}
